import { useState } from "react";
import { bool, number, string, func, node, arrayOf, oneOfType } from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, TextField, Dialog, Checkbox } from "@components";
import useStyle from "./Alert.style.js";
import Grid from "@material-ui/core/Grid";
import { CircularProgress } from "@material-ui/core";

export const Alert = ({
  title,
  children,
  acceptText,
  cancelText,
  intermediateText,
  isAcceptLoading,
  isCancelLoading,
  isIntermediateLoading,
  isOpen,
  onAccept,
  onIntermediate,
  onCancel,
  confirmationTitle,
  confirmationValue,
  actions: otherActions,
  useLockCheckbox,
  flipActionOrder,
  width,
  ...dialogProps
}) => {
  const {t} = useTranslation();
  const classes = useStyle();
  
  const [ value, setValue ] = useState("");
  const [ lock, setLock ] = useState(false);
  const [ disabled, setDisabled ] = useState(true);
  
  const handleAccept = (e) => {
    if(isAcceptLoading || isIntermediateLoading || isCancelLoading)
      return;
    onAccept && onAccept(e, useLockCheckbox ? lock : undefined);
  };
  
  const handleCancel = (e) => {
    if(isAcceptLoading || isIntermediateLoading || isCancelLoading)
      return;
    onCancel && onCancel(e, useLockCheckbox ? lock : undefined);
  };
  
  const handleIntermediate = (e) => {
    if(isAcceptLoading || isIntermediateLoading || isCancelLoading)
      return;
    onIntermediate && onIntermediate(e, useLockCheckbox ? lock : undefined);
  };
  
  const handleChange = e => {
    const val = e.currentTarget.value;
    setValue(val);
    setDisabled(val !== confirmationValue);
  };
  const handleLockChange = () => {
    setLock(!lock);
  };
  
  const useIntermediate = Boolean(onIntermediate && intermediateText);
  const useAccept = Boolean(onAccept);
  const useCancel = Boolean(onCancel);
  
  const actions = <>
    {
      useLockCheckbox &&
      <Checkbox
        name="lockOption"
        checked={lock}
        className="mr-2"
        onChange={handleLockChange}
        label={t("common.not_again")}
      />
    }
    {otherActions}
    {
      (useCancel || useAccept || useIntermediate || otherActions) &&
      <Grid
        item container
        justifyContent="flex-end"
        direction={ flipActionOrder ? "row-reverse" : "row" }
        className="w-max"
      >
        {
          useCancel && <Button
            variant="outlined"
            className={classes.button}
            onClick={handleCancel}
            endIcon={isCancelLoading && <CircularProgress size={18} />}
          >
            {cancelText || t("common.cancel")}
          </Button>
        }
        {
          useIntermediate &&
          <Button
            variant="outlined"
            className={classes.button}
            disabled={confirmationTitle && confirmationValue && disabled}
            onClick={handleIntermediate}
            endIcon={isIntermediateLoading && <CircularProgress size={18} />}
          >
            {intermediateText}
          </Button>
        }
        {
          useAccept &&
          <Button
            variant="contained"
            className={classes.button}
            disabled={confirmationTitle && confirmationValue && disabled}
            onClick={handleAccept}
            endIcon={isAcceptLoading && <CircularProgress size={18} color="inherit" />}
          >
            {acceptText || t("common.accept")}
          </Button>
        }
      </Grid>
    }
  </>;
  
  const content = children || (confirmationTitle && confirmationValue) && <>
    {
      children && (
        typeof children === "string" ?
          <p className={classes.subText}>
            {children}
          </p> :
          children
      )
    }
    {
      !!(confirmationTitle && confirmationValue) &&
      <>
        <p className="mb-2">
          {confirmationTitle}
        </p>
        <TextField
          value={value}
          id="confirm-action"
          onChange={handleChange}
        />
      </>
    }
  </>;
  
  return <Dialog
    open={isOpen}
    onClose={useCancel ? handleCancel : handleAccept}
    actions={actions}
    title={title}
    titleClass="text-lg"
    width={width || (useIntermediate ? 540 : 380)}
    useCloseButton={false}
    actionsAlignment={useLockCheckbox ? "spaceBetween" : "end"}
    {...dialogProps}
  >
    {content}
  </Dialog>;
};

Alert.propTypes = {
  title: oneOfType([string, node]),
  children: oneOfType([string, node]),
  acceptText: oneOfType([string, node]),
  intermediateText: string,
  cancelText: oneOfType([string, node]),
  isAcceptLoading: bool,
  isCancelLoading: bool,
  isIntermediateLoading: bool,
  isOpen: bool,
  onAccept: func,
  onIntermediate: func,
  onCancel: func,
  confirmationTitle: string,
  confirmationValue: string,
  actions: oneOfType([
    arrayOf(node),
    node
  ]),
  useLockCheckbox: bool,
  flipActionOrder: bool,
  width: number,
};
